import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import type { SequenceTableRow } from '../types';

export const SEQUENCE_EDIT_MODAL_WIDTH = 'max-w-lg';

interface SequenceEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: SequenceTableRow | null;
  onSave?: (row: SequenceTableRow, values: Partial<SequenceTableRow>) => void | Promise<void>;
  loading?: boolean;
}

export const SequenceEditModal: React.FC<SequenceEditModalProps> = ({
  open,
  onOpenChange,
  row,
  onSave,
  loading = false,
}) => {
  const [pipeStart, setPipeStart] = useState('');
  const [pipeEnd, setPipeEnd] = useState('');

  React.useEffect(() => {
    if (row && open) {
      setPipeStart(row.pipeStart ?? '');
      setPipeEnd(row.pipeEnd ?? '');
    }
  }, [row, open]);

  const handleSave = () => {
    if (!row) return;
    const values = { pipeStart: pipeStart.trim() || undefined, pipeEnd: pipeEnd.trim() || undefined };
    if (onSave) {
      Promise.resolve(onSave(row, values)).then(() => onOpenChange(false)).catch(() => {});
    } else {
      onOpenChange(false);
    }
  };

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={SEQUENCE_EDIT_MODAL_WIDTH}>
        <DialogHeader>
          <DialogTitle>Edit Sequence</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Pipe ID: <span className="font-medium">{row.pipeId}</span>
          </p>
          <div className="space-y-2">
            <Label htmlFor="edit-pipe-start">Pipe Start</Label>
            <Input
              id="edit-pipe-start"
              value={pipeStart}
              onChange={(e) => setPipeStart(e.target.value)}
              disabled={loading}
              placeholder="Pipe start"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-pipe-end">Pipe End</Label>
            <Input
              id="edit-pipe-end"
              value={pipeEnd}
              onChange={(e) => setPipeEnd(e.target.value)}
              disabled={loading}
              placeholder="Pipe end"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading} loading={loading} loadingText="Saving...">
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
